import { Dispatch, FC, ReactNode, RefObject, SetStateAction, useMemo, useState } from "react";
import { App, Button, Flex, message, notification } from "antd";
import { useTranslation } from "react-i18next";
import {
  RequirementAdd,
  RequirementAddItemUseRepl,
  RequirementConfirmShowEnum,
  RequirementConfirmTypeEnum,
  RequirementTemplateAdd,
} from "@/types/requirement";
import RequirementSteps from "@/pages/Requirement/components/RequirementSteps";
import { RequirementColumn } from "@/pages/Requirement/type";
import requirementApis from "@/apis/requirement";
import { SaleItemState, SaleOrderItemState } from "@/types/sales";
import { SourceTypeEnum } from "@/types/common";
import { Bom } from "@/types/bom";
import useAddRequirement from "@/pages/Requirement/hooks/useAddRequirement";
import { getRequirementConfirmItem } from "@/pages/Requirement/util";
import { RequirementFieldType } from "@/pages/Requirement/components/RequirementForm";
import { ChangeEnum, OperateEnum } from "@/types/inventory";
import { MatchedRequirementColumn } from "..";
import { orderAdd, orderPreOut } from "@/apis/inventoryv2";
import { OrderAddV2 } from "@/types/inventoryV2";
import { ProdInputRef } from "../../Detail/components/ProdInput";
import { AddFactoryInventory, FactoryInventorySourceType } from "@/types/factoryInventory";
import { fInventoryAdjust } from "@/apis/factoryInventory";
import { roundToDecimal } from "@/utils/calc";


interface ConRequirementColumn extends RequirementColumn {
  stockNum?: number;  //库存数量
  stockPreOut?: number;  //库存预出库数量
  useNum?: number;  //可用库存数量
  purCount?: number;  //最终的amount[需采购]
  preOutNum?: number;  //预出库数量
}
interface CreateFooterProps {
  isTemp: boolean;
  item: SaleItemState;
  detail: Bom | undefined;
  bomSubs: Array<RequirementColumn>;
  purchaseProducts: Array<RequirementColumn>;
  setPurchaseProducts: Dispatch<SetStateAction<Array<RequirementColumn>>>;
  matchedProducts: Array<MatchedRequirementColumn>;
  setMatchedProducts: Dispatch<SetStateAction<Array<MatchedRequirementColumn>>>;
  confirmProducts: Array<RequirementColumn>;
  setConfirmProducts: Dispatch<SetStateAction<Array<ConRequirementColumn>>>;
  formData: RequirementFieldType | undefined;
  setFormData: Dispatch<SetStateAction<RequirementFieldType | undefined>>;
  steps: Array<{
    title: string;
    key: string;
    content?: ReactNode;
  }>;
  handleAdd: () => void;
  setNumber: Dispatch<SetStateAction<string>>;
  current: number;
  setCurrent: Dispatch<SetStateAction<number>>;
  requireCount: number;
  disNext?: boolean;
  setDisNext?: () => void;
  matchStock?: boolean;
  setMatchStock?: () => void;
  isMerge?: boolean;
  prodInputRef?: RefObject<ProdInputRef>;
  saleOrder: SaleOrderItemState;
  preOutDesc: string;
  preOutLocationTypes: number[];
  locTypes: number[];
}


const CreateFooter: FC<CreateFooterProps> = ({
  item,
  purchaseProducts,
  detail,
  bomSubs,
  setPurchaseProducts,
  setConfirmProducts,
  steps,
  matchedProducts,
  setMatchedProducts,
  confirmProducts,
  formData,
  setFormData,
  isTemp,
  handleAdd,
  setNumber,
  current,
  setCurrent,
  requireCount,
  disNext = false,
  setDisNext = () => { },
  matchStock = false,
  setMatchStock = () => { },
  isMerge = false,
  prodInputRef,
  saleOrder,
  preOutDesc,
  preOutLocationTypes,
  locTypes,
}) => {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const [add, addLoading] = useAddRequirement();

  const [openConfirm, setOpenConfirm] = useState<boolean>(false);

  const isImported = useMemo(() => {
    return !!purchaseProducts.find(item => item.product && item.product.id === item.product.code)
  }, [purchaseProducts])

  const handleNext = () => {
    if (!matchStock) setCurrent(current + 1);
    else {
      const nextProducts = isMerge
        ? Array.from(purchaseProducts.reduce((map, pro) => {
          const code = pro.product?.code ?? "";
          const existing = map.get(code);
          map.set(code, existing ? {
            ...existing,
            useAmount: (Number(existing.useAmount) || 0) + (Number(pro.useAmount) || 0),
            baseQuantity: (Number(existing.baseQuantity) || 0) + (Number(pro.baseQuantity) || 0),
          } : pro);
          return map;
        }, new Map<string, RequirementColumn>()).values())
        : purchaseProducts;

      const conirmData: MatchedRequirementColumn[] = nextProducts.map(pro => ({
        ...pro,
        stockNum: 0,
        stockPreOut: 0,
        useNum: 0,
        allocated: 0,
        purCount: pro.useAmount,
        preOutNum: 0,
        preIn: 0,
        matchKey: '',
        useFactoryCount: 0,
        factoryCount: 0,
        factoryAllocated: 0,
      }));
      setMatchedProducts([...conirmData]);
      setConfirmProducts([...conirmData]);
      setCurrent(4);
    }
  }

  const addTemplate: (toNxt?: boolean) => void = (toNxt = false) => {
    const data: RequirementTemplateAdd = purchaseProducts.map((item) => ({
      productId: item.product?.id ?? "",
      useAmount: Number((item?.useQuantity ?? "0").toString().slice(0, 10)),
      desc: "",
      attritionRate: (item?.attritionRate ?? "0").toString(),
      baseCount: (item?.baseQuantity ?? "0").toString(),
      extCount: (item.extCount ?? "0").toString(),
    }));
    requirementApis.tempSave(item.productId, data).then((res) => {
      notification.success({ placement: 'top', message: t("requirement.saveTempSuccess") });
      if (toNxt) handleNext();
    });
  };

  const next = () => {
    if (current === 0) {
      setCurrent(1);
      setPurchaseProducts(
        bomSubs.map((bom) => ({
          ...bom,
          useAmount: Math.ceil(bom.useAmount),
          bomRequireCount: Math.ceil(bom.useAmount),
        }))
      );

    } else if (current == 1) {
      for (let i = 0; i < purchaseProducts.length; i++) {
        const data = purchaseProducts[i];
        if (!!data.beReplaced) {
          let repCount = 0;
          purchaseProducts.filter(sub => sub.replaceParentId === data.product.id)
            .forEach(sub => {
              repCount += Number(sub.useAmount || '')
            })
          if (Number(data.bomRequireCount || '') !== Number(data.useAmount || '') - Number(data.extCount || '') + repCount) {
            notification.error({
              message: t('bom.calcErr'),
              description: (
                <>
                  <div>{t('bom.calcData')}</div>
                  <div style={{ color: "red" }}>{data.product.code}</div>
                </>
              ),
              duration: 10
            });
            return;
          }
        }
      }
      let resData = purchaseProducts.filter((item) => item.useAmount !== 0 || !!item.bomItemId)
      if (resData.length === 0) notification.error({ placement: 'top', message: t('bom.errTip'), description: t('bom.errTipdesc') })
      else {
        isImported && handleNext();
        !isImported && modal.confirm({
          title: t('common.tip'),
          content: t('bom.saveTemp'),
          onOk() {
            addTemplate(true)
          },
          onCancel() {
            handleNext();
          },
        })
      }
    } else if (current === 2) {
      let errRows = matchedProducts.filter(mp => mp.allocated > mp.useNum).map(r => r.product.code)
      if (errRows.length > 0) return notification.error({
        message: t('requirement.overTip'),
        description: Array.from(new Set(errRows)).join(', ')
      })
      setConfirmProducts([...matchedProducts])
      setCurrent(current + 1);
    } else if (current === 3 && [...locTypes, ...preOutLocationTypes].length === 0) {
      notification.warning({
        placement: 'top',
        message: '请选择出库匹配库位类型',
      });
    } else {
      setCurrent(current + 1);
    }
  };

  const prev = () => {
    if (current === 4) {
      if (matchStock) setCurrent(1);
      else setCurrent(current - 1);
    } else setCurrent(current - 1);
    setDisNext()
  };

  const addRequirement = (val: string) => {
    const addInfo: Partial<RequirementAdd> = {
      sourceType: SourceTypeEnum.SALE,
      sourceId: saleOrder.id,
      productId: saleOrder.productId || undefined,
      bomHisId: detail?.id ?? "",
      desc: formData?.tip || "",
      requireCount: (requireCount ?? 0).toString(),
      mrd: formData?.mrd,
      eps: formData?.eps,
      preFinishDate: formData?.preFinishDate,
      items: matchedProducts.filter(item => !!item?.useAmount && item.useAmount > 0).map((item) => {
        let baseQuantityNumber = roundToDecimal(Number(item?.baseQuantity) || 0);
        return {
          productId: item.product.id,
          bomItemId: item.bomItemId, // bomId
          bomReplId: item.bomReplId, // 替代料的bomId
          useRepl: RequirementAddItemUseRepl.NOT_USE,
          maxEstimatedPriceId: "",
          minEstimatedPriceId: "",
          maxEstimatedCost: "",
          minEstimatedCost: "",
          useAmount: !!item?.useAmount ? item.useAmount + '' : '', // 使用数量
          desc: "",
          purCount: (item.purCount ?? 0).toString(),
          puredCount: "0",
          outCount: "0",
          ert: null,
          extCount: (item.extCount ?? 0).toString(),
          supplierId: "",
          attritionRate: (item?.attritionRate ?? 0).toString(),
          baseCount: baseQuantityNumber === 0 ? '' : baseQuantityNumber + '',
          matchKey: item.matchKey,
          bomAmount: baseQuantityNumber === 0 ? '' : baseQuantityNumber + '',
          process: item.bomProcess,
          useFactoryCount: item.useFactoryCount || 0
        }
      }),
      confirms: getRequirementConfirmItem(
        (formData?.steps || []).map((item) => ({
          ...item,
          value: null,
          desc: "",
        })),
        RequirementConfirmTypeEnum.MATERIAL_CONTROL,
        "next",
        val
      ),
    };
    add(addInfo, (res) => {
      const requirementItems = [...res.items];
      const factoryOut: Record<string, number> = {};
      requirementItems.forEach(r => {
        if (!!factoryOut[r.productId]) factoryOut[r.productId] -= (Number(r.useFactoryCount) || 0)
        else factoryOut[r.productId] = 0 - (Number(r.useFactoryCount) || 0)
      });
      const facInfo: AddFactoryInventory = {
        sourceType: FactoryInventorySourceType.REQUIREMENT,
        sourceId: res.id,
        items: Object.entries(factoryOut).map(([productId, count]) => ({ productId, count })).filter(i => i.count !== 0),
        desc: ''
      };
      if (facInfo.items.length > 0) {
        fInventoryAdjust(facInfo)
          .catch((error: any) => {
            let description = '';
            if (error?.code === 70873) {
              let product = matchedProducts.find(p => p.product.id === error?.extra?.productId)?.product!
              description = t('apiError.error31') + ': ' + product.code
            }
            notification.error({ message: t('warehouse.outFail'), description })
          })
      }
      let order: OrderAddV2 = {
        arrivalAt: null,
        preTime: null,
        type: ChangeEnum.PRE_OUT,
        opType: OperateEnum.PICKING,
        desc: preOutDesc,
        items: matchedProducts.filter(i => i.preOutNum && i.preOutNum > 0 && i.product.preReady === 0)
          .map(item => ({
            sourceType: SourceTypeEnum.REQUIREMENT_ITEM,
            sourceId: requirementItems.find(rtm => rtm.matchKey === item.matchKey)?.id ?? '',
            productId: item.product.id,
            count: item.preOutNum,
            desc: '',
            lot: '',
            thdlot: '',
            locations: [],
          })),
      }
      if (order.items.length > 0) {
        (async () => {
          const res = await orderAdd(order);
          await orderPreOut(res?.id ?? '', locTypes.length > 0 ? locTypes : preOutLocationTypes);
          notification.success({ placement: 'top', message: t('requirement.createPreOutInventorySuccess'), description: t('bom.inventorySucc', { number: res.number }) });
        })().catch((err: any) => {
          if (err?.code === 73010) {
            notification.error({
              message: t('inventory.proQtyFalse'),
              description: err?.extra?.map((i: { productId: string }) => i.productId)?.join(', ')
            })
          } else if ([73022, 73014].includes(err)) {
            notification.error({
              message: t('inventory.calcErr')
            })
          } else if ([73024, 73028].includes(err?.code)) {
            notification.error({
              message: t('warehouse.insufficientInventory'),
              description: err?.extra?.productCode
            })
          }
          else notification.error({ placement: 'top', message: t('requirement.createPreOutInventoryFail'), description: t('requirement.doneSuccess') })
        })
      }
      setNumber(res.number);
      setCurrent(current + 1);
      handleAdd();
    });
  };

  const handleConfirm = () => {
    const hasPreOutItems = matchedProducts.some(i => i.preOutNum && i.preOutNum > 0 && i.product.preReady === 0);
    if (hasPreOutItems && [...locTypes, ...preOutLocationTypes].length === 0) {
      return notification.warning({
        placement: 'top',
        message: '请选择出库匹配库位类型',
      });
    }

    const needProd = formData?.steps?.find(s => s.type === RequirementConfirmTypeEnum.PROD && s.show === RequirementConfirmShowEnum.HIDDEN)
    if (needProd && prodInputRef?.current?.getValues()) {
      const { mrd, eps, preFinishDate } = prodInputRef?.current?.getValues();
      if (!mrd) {
        return notification.error({ placement: 'top', message: t("requirement.plMrd") });
      }
      if (!eps) {
        return notification.error({ placement: 'top', message: t("requirement.plEps") });
      }
      if (!preFinishDate) {
        return notification.error({ placement: 'top', message: t("sale.plPreFinishDate") });
      }
      setFormData(last => {
        setOpenConfirm(true)
        return {
          ...last,
          eps: eps.endOf('day').valueOf(),
          mrd: mrd.endOf('day').valueOf(),
          preFinishDate: preFinishDate.endOf('day').valueOf()
        }
      })

    } else setOpenConfirm(true)
  }

  return (
    <>
      <Flex style={{ width: "100%", position: "absolute", bottom: "4px" }} justify="space-between">
        <div>
          {current < steps.length - 2 && (
            <Button type="primary" onClick={() => next()} disabled={disNext}>
              {t("requirement.next")}
            </Button>
          )}
          {current === steps.length - 2 && (
            <Button
              loading={addLoading}
              type="primary"
              onClick={handleConfirm}
            >
              {t("common.add")}
            </Button>
          )}
          {current === 1 && <Button
            disabled={addLoading || (current === 1 && !!!saleOrder.productId)}
            style={{ margin: "0 8px" }}
            onClick={() => setCurrent(0)}
          >
            {t("requirement.previous")}
          </Button>}
          {current !== 1 && ((current > 0 && current !== steps.length - 1 && !isTemp) ||
            (current > 1 && isTemp)) && (
              <Button
                disabled={addLoading || (current === 1 && !!!saleOrder.productId)}
                style={{ margin: "0 8px" }}
                onClick={() => prev()}
              >
                {t("requirement.previous")}
              </Button>
            )}
        </div>
        {current === 1 && !isImported && (
          <Button type="primary" onClick={() => addTemplate()}>
            {t("requirement.saveTemp")}
          </Button>
        )}
      </Flex>
      <RequirementSteps
        id={""}
        visible={openConfirm}
        confirms={[]}
        onClose={() => setOpenConfirm(false)}
        onOk={(val) => {
          addRequirement(val ?? "");
          setOpenConfirm(false);
        }}
      />
    </>
  );
};

export default CreateFooter;
